import React, { useEffect, useState } from 'react'
import { Avatar, Button, TextField, CircularProgress } from '@mui/material'
import { FaSteam } from 'react-icons/fa'
import { toast } from 'react-toastify'
import BaseModal from '../../../../utils/BaseModal'
import api from '../../../../../api/axios'

type Props = {
  open: boolean
  steamAccountId?: number
  steamAccountSteamId?: string
  steamName?: string
  avatarUrl?: string
  tradeUrl?: string
  onClose: () => void
  onSaved: (tradeUrl: string) => void
}

const EditTradeUrlModal: React.FC<Props> = ({
                                              open,
                                              steamAccountId,
                                              steamAccountSteamId,
                                              steamName,
                                              avatarUrl,
                                              tradeUrl,
                                              onClose,
                                              onSaved
                                            }) => {
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)

  const STEAM_TRADE_URL_PREFIX = 'https://steamcommunity.com/tradeoffer/new/?partner='

  // Validation: must start with prefix and contain more than just the prefix
  const isValid = value.trim().startsWith(STEAM_TRADE_URL_PREFIX) && value.length > STEAM_TRADE_URL_PREFIX.length

  useEffect(() => {
    setValue(tradeUrl ?? '')
  }, [tradeUrl, open])

  // Construct dynamic Steam URL based on the provided steamAccountSteamId prop
  const steamTradeUrlLink = `https://steamcommunity.com/profiles/${steamAccountSteamId}/tradeoffers/privacy#trade_offer_access_url`

  const handleSave = async () => {
    if (!steamAccountId || !isValid) return

    setSaving(true)
    try {
      await api.put(`/profile/linked-accounts/trade-url`, {
        steamAccountId: steamAccountId,
        tradeUrl: value
      })

      toast.success('Trade-URL erfolgreich gespeichert')
      onSaved(value)
      onClose()
    } catch (e: any) {
      // Error handling with fallback message
      toast.error(e?.response?.data)
    } finally {
      setSaving(false)
    }
  }

  const handleOpenSteam = () => {
    window.open(steamTradeUrlLink, '_blank', 'noopener,noreferrer')
  }

  return (
      <BaseModal
          open={open}
          title={tradeUrl ? 'Trade-URL bearbeiten' : 'Trade-URL hinzufügen'}
          abortText="Abbrechen"
          submitText={
            <div className="flex items-center gap-2">
              {saving && <CircularProgress size={16} color="inherit"/>}
              <span>Confirm</span>
            </div>
          }
          onAbort={onClose}
          onSubmit={handleSave}
          maxWidth="sm"
          // Button is disabled if saving, empty, or invalid format
          submitDisabled={saving || !value || !isValid}
          abortDisabled={saving}
      >
        <div className="flex items-center gap-4 mb-6">
          <Avatar src={avatarUrl} className="w-14 h-14"/>
          <div className="text-lg font-medium">{steamName}</div>
        </div>

        <div className="flex gap-2 items-start">
          <TextField
              label="Steam Trade-URL"
              fullWidth
              value={value}
              onChange={e => setValue(e.target.value)}
              disabled={saving}
              size="small"
              error={value.length > 0 && !isValid}
              helperText={value.length > 0 && !isValid ? 'Invalid Steam Trade-URL' : ''}
              slotProps={{
                input: {
                  className: "h-11"
                }
              }}
          />

          <Button
              variant="contained"
              onClick={handleOpenSteam}
              disabled={saving}
              // Keep button aligned despite possible helperText expansion
              className="h-11 flex-shrink-0 flex gap-2 px-6 min-w-[160px] whitespace-nowrap shadow-none normal-case"
          >
            <FaSteam className="text-lg"/>
            Get Trade-URL
          </Button>
        </div>
      </BaseModal>
  )
}

export default EditTradeUrlModal